import {
  getFontCss,
  Popup,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import {
  selectRemovingBudgetUser,
  selectRemovingBudgetUserTopLevelError,
} from 'src/app/BudgetOverview/redux/budgetOverview.selectors'
import { ButtonDesigns } from 'src/components/Button/Button'
import ConfirmationPopupContent from 'src/components/ConfirmationPopupContent/ConfirmationPopupContent'
import { useTranslate } from 'src/localization/Trns'
import { budgetOverviewUrl } from 'src/navigation/urls'
import { createSetIsRemoveBudgetUserInvitePopupOpen } from 'src/redux/actions/app.actions'
import { deleteBudgetUsers } from 'src/redux/actions/data/creditCards.actions'
import { selectUserIdFromState } from 'src/redux/selectors/auth.selectors'
import {
  selectBudget,
  selectBudgetUser,
} from 'src/redux/selectors/data/creditCards.selectors'
import { selectUserListModelsFromState } from 'src/redux/selectors/data/users.selectors'
import { css } from 'styled-components'

const RemoveBudgetUserPopup: React.FC = (): React.ReactElement => {
  const translate = useTranslate()

  const dispatch = useDispatch()
  const removingBudgetUser = useSelector(selectRemovingBudgetUser)
  const removingBudgetUserTopLevelError = useSelector(
    selectRemovingBudgetUserTopLevelError,
  )

  const budget = useSelector(selectBudget)
  const budgetUser = useSelector(selectBudgetUser)
  const userId = useSelector(selectUserIdFromState)
  const users = useSelector(selectUserListModelsFromState)
  const { analytics } = useAnalyticsContext()

  const currentUser = userId !== undefined ? users[userId] : undefined

  const dispatchRemoveBudgetUser = (): void => {
    if (budget === undefined || budgetUser === undefined) return
    dispatch(
      deleteBudgetUsers(
        budget.id,
        [budgetUser.email],
        budgetUser.firstName,
        budgetUser.lastName,
      ),
    )
    analytics?.track(
      'budget details page - remove a budget user - button press',
      {
        interaction: 'button press',
        intent: 'remove a budget user',
        screen: budgetOverviewUrl(budget?.id),
        typeOfBudget: budget.recurrence
          ? 'Recurring'
          : budget.isLimitedBudget
            ? 'Standard'
            : 'Unlimited',
        budgetName: budget.displayName,
        numberOfRemainingBudgetUsers: budget?.budgetUsers?.length,
        userRemoved: budgetUser.email,
        isRemovedMe: budgetUser.id === currentUser?.id,
      },
    )
  }

  const closePopup = (): void => {
    dispatch(createSetIsRemoveBudgetUserInvitePopupOpen(false))
  }

  return (
    <Popup
      onClickClose={closePopup}
      showCloseButton
      css={`
        display: flex;
        flex-direction: column;
        position: relative;
      `}
    >
      <ConfirmationPopupContent
        title={translate('Remove last budget user')}
        subtitle={
          <>
            <p
              css={css`
                margin-bottom: 28px;
              `}
            >
              {translate(
                'Budgets must always have at least one user. As the budget creator, removing the last user will automatically make you a budget user.',
              )}
            </p>
            <p>
              {translate(
                'Are you sure you want to remove ${budgetUser} from ${budgetName}?',
                {
                  budgetUser: (
                    <span
                      css={css`
                        ${getFontCss(
                          16,
                          themeConstants.colors.font_primary,
                          styleConstants.fontFamilies.medium,
                        )};
                      `}
                    >
                      {budgetUser?.firstName} {budgetUser?.lastName}
                    </span>
                  ),
                  budgetName: (
                    <span
                      css={css`
                        ${getFontCss(
                          16,
                          themeConstants.colors.font_primary,
                          styleConstants.fontFamilies.medium,
                        )};
                      `}
                    >
                      {budget?.displayName}
                    </span>
                  ),
                },
              )}
            </p>
          </>
        }
        actionButtonLabel={translate('Confirm Removal')}
        actionButtonOnClick={dispatchRemoveBudgetUser}
        actionButtonLoading={removingBudgetUser}
        actionButtonDesign={ButtonDesigns.designRed}
        topLevelError={removingBudgetUserTopLevelError}
        isCancelButtonLink
        underline={false}
        cancelButtonOnClick={closePopup}
        cancelButtonLabel={translate('Cancel')}
      />
    </Popup>
  )
}

export default RemoveBudgetUserPopup
